<template>
  <header class="fixed top-0 w-full z-50 transition-all duration-500 py-4"
          :class="[
            currentPage === 'home' && !isScrolled
              ? 'bg-transparent' 
              : 'bg-white/90 backdrop-blur-md shadow-lg'
          ]">
    <div class="container-custom flex items-center justify-between relative z-10">
      <!-- Logo Group - Unified Original Color -->
      <div class="flex items-center space-x-3 lg:space-x-5 cursor-pointer shrink-0 group" @click="$emit('navigate', 'home')">
        <img src="../assets/images/logo.png" alt="Logo" class="h-8 lg:h-10 object-contain">
        <div class="w-px h-6" :class="currentPage === 'home' && !isScrolled ? 'bg-white/30' : 'bg-slate-300'"></div>
        <img :src="$i18n.locale === 'zh' ? require('../assets/images/logo_zh.png') : require('../assets/images/logo_eng.png')" 
             :alt="$i18n.locale === 'zh' ? 'Logo Chinese' : 'Logo English'" 
             class="h-5 lg:h-6 object-contain"
             :class="{ 'brightness-0 invert': currentPage === 'home' && !isScrolled }">
      </div>

      <!-- Navigation - Unified Style -->
      <nav class="hidden lg:flex items-center space-x-1">
        <a v-for="item in navItems" :key="item.id"
           href="javascript:void(0)" 
           class="px-5 py-2 rounded-xl text-[15px] transition-all duration-300 relative group/nav tracking-wide"
           :class="[
             currentPage === 'home' && !isScrolled
               ? (currentPage === item.id ? 'text-white font-bold bg-white/10' : 'text-white/80 font-medium hover:text-white hover:bg-white/5')
               : (currentPage === item.id ? 'text-[#C6202B] font-bold bg-[#C6202B]/5' : 'text-gray-600 font-medium hover:text-[#C6202B] hover:bg-gray-50')
           ]"
           @click="$emit('navigate', item.id)">
          {{ $t(`common.nav.${item.id}`) }}
        </a>
        
        <!-- Language Switcher -->
        <div class="flex items-center ml-6 pl-6 border-l" :class="currentPage === 'home' && !isScrolled ? 'border-white/20' : 'border-gray-200'">
          <button @click="toggleLanguage" 
                  class="px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-300 border-2 uppercase tracking-widest"
                  :class="[
                    currentPage === 'home' && !isScrolled
                      ? 'border-white text-white hover:bg-white hover:text-gray-900'
                      : 'border-[#C6202B] text-[#C6202B] hover:bg-[#C6202B] hover:text-white'
                  ]">
            {{ $i18n.locale === 'zh' ? 'English' : '中文' }}
          </button>
        </div>
      </nav>

      <!-- Mobile Menu Button -->
      <button class="lg:hidden p-2 rounded-lg transition-colors"
              :class="currentPage === 'home' && !isScrolled ? 'text-white hover:bg-white/10' : 'text-gray-800 hover:bg-gray-100'"
              @click="isMobileMenuOpen = !isMobileMenuOpen">
        <svg v-if="!isMobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-7 6h7"></path></svg>
        <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
      </button>
    </div>

    <!-- Mobile Menu -->
    <transition enter-active-class="transition duration-300 ease-out"
                enter-from-class="opacity-0 -translate-y-4"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-4">
      <div v-if="isMobileMenuOpen" class="lg:hidden absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 overflow-hidden">
        <nav class="px-4 py-6 space-y-1">
          <a v-for="item in navItems" :key="item.id"
             href="javascript:void(0)" 
             class="block px-6 py-3 rounded-xl text-base font-medium transition-all"
             :class="currentPage === item.id ? 'bg-[#C6202B]/10 text-[#C6202B]' : 'text-gray-600 hover:bg-gray-50'"
             @click="$emit('navigate', item.id); isMobileMenuOpen = false">
            {{ $t(`common.nav.${item.id}`) }}
          </a>
          <div class="pt-4 mt-4 border-t border-gray-100 px-6">
             <button @click="toggleLanguage(); isMobileMenuOpen = false" 
                     class="w-full py-3 rounded-xl bg-gray-900 text-white text-sm font-bold tracking-wider uppercase">
               {{ $i18n.locale === 'zh' ? 'Switch to English' : '切换为中文' }}
             </button>
          </div>
        </nav>
      </div>
    </transition>
  </header>
</template>

<script>
export default {
  name: 'TheHeader',
  props: {
    currentPage: {
      type: String,
      default: 'home'
    }
  },
  data() {
    return {
      isScrolled: false,
      isMobileMenuOpen: false,
      navItems: [
        { id: 'home' },
        { id: 'about' },
        { id: 'partners' },
        { id: 'equipment' },
        { id: 'product' },
        { id: 'specs' },
        { id: 'contact' }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll(); // 初始化检查一次
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    toggleLanguage() {
      const newLang = this.$i18n.locale === 'zh' ? 'en' : 'zh';
      this.$i18n.locale = newLang;
      localStorage.setItem('lang', newLang);
      this.$root.$emit('language-changed', newLang);
    }
  }
}
</script>

